import React, { useEffect, useRef, useState } from "react";

/**
 * Scroll-driven alignment cue.
 *
 * - Renders 9 thin gold marks with randomised vertical offsets (out of alignment).
 * - As the user scrolls past the hero, every mark eases toward y=0 (in alignment).
 * - The hero subline above (passed via children) becomes the alignment "answer";
 *   the marks snap under it as scroll progresses.
 *
 * Honors `prefers-reduced-motion` (renders the aligned final state, no animation).
 */
// Stable per-mark seeds. Each entry's identity is its key — using an `id`
// avoids the "array index as key" pattern even though the list is fixed-length
// and never reorders.
const MARKS = [
  { id: "m1", offset: -22, delay: 0 },
  { id: "m2", offset: 14, delay: 0.05 },
  { id: "m3", offset: -10, delay: 0.1 },
  { id: "m4", offset: 26, delay: 0.15 },
  { id: "m5", offset: -18, delay: 0.2 },
  { id: "m6", offset: 8, delay: 0.25 },
  { id: "m7", offset: 20, delay: 0.3 },
  { id: "m8", offset: -14, delay: 0.35 },
  { id: "m9", offset: 12, delay: 0.4 },
];
const CENTER_INDEX = Math.floor(MARKS.length / 2);

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const AlignmentCue = () => {
  const wrapRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      setProgress(1);
      return undefined;
    }

    let raf = 0;
    const compute = () => {
      const el = wrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // 0 when the cue sits at the bottom of the viewport,
      // 1 when the cue has been pushed off-screen by ~50% of viewport height.
      const raw = 1 - Math.max(0, Math.min(1, (rect.top + rect.height * 0.5) / vh));
      // Bias the curve so most of the alignment happens in the early portion of scroll.
      const eased = easeOutCubic(Math.min(1, raw * 2));
      setProgress(eased);
    };

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      data-testid="hero-alignment-cue"
      aria-hidden="true"
      className="mt-12 flex flex-col items-center select-none"
      style={{ "--align-progress": progress }}
    >
      {/* Marks row */}
      <div className="relative flex items-center justify-center gap-3 h-12">
        {MARKS.map((m, i) => {
          const offset = m.offset * (1 - progress);
          const opacity = 0.35 + 0.55 * progress;
          const isCenter = i === CENTER_INDEX;
          return (
            <span
              key={m.id}
              className="block rounded-full transition-[transform,opacity,background] duration-200 ease-out"
              style={{
                width: isCenter ? 28 : 14,
                height: 1.5,
                transform: `translateY(${offset.toFixed(2)}px)`,
                background: isCenter
                  ? "linear-gradient(90deg, rgba(243,229,171,0) 0%, #F3E5AB 50%, rgba(243,229,171,0) 100%)"
                  : "linear-gradient(90deg, rgba(212,175,55,0.8), rgba(212,175,55,0.35))",
                opacity,
                transitionDelay: `${m.delay}s`,
                boxShadow: progress > 0.6 ? "0 0 6px rgba(212,175,55,0.6)" : "none",
              }}
            />
          );
        })}
      </div>

      <span
        className="mt-3 text-[10px] tracking-[0.4em] uppercase transition-colors duration-700"
        style={{
          color: `rgba(212, 175, 55, ${0.5 + 0.45 * progress})`,
        }}
      >
        Scroll
      </span>
    </div>
  );
};

export default AlignmentCue;
